'use client';

import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import type { VtkDatasetInfo, VtkPolylineSet } from '../types/vtk';
import { loadVtkModules } from '../lib/loadVtkModules';
import {
  createVtkScene,
  type CameraState,
  type VtkScene,
} from '../lib/createVtkScene';
import {
  applyPaletteToMapper,
  replaceScalarBar,
  resolveScalarRange,
  type ScalarRange,
} from '../lib/vtkScalarStyling';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type VtkRenderer = any;

export type VtkViewerHandle = {
  resetCamera: () => void;
};

type Props = {
  dataset: VtkDatasetInfo | null;
  activeScalar?: string | null;
  activeScalarLabel?: string | null;
  streamlines?: VtkPolylineSet | null;
  streamlineScalar?: string | null;
  streamlineScalarLabel?: string | null;
  showStreamlines?: boolean;
  meshVisible?: boolean;
  status?: 'ready' | 'computing';
  forcedScalarRange?: ScalarRange | null;
};

function captureCamera(renderer: VtkScene['renderer']): CameraState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cam = (renderer as any).getActiveCamera();
  return {
    pos: [...cam.getPosition()],
    fp: [...cam.getFocalPoint()],
    up: [...cam.getViewUp()],
    cr: [...cam.getClippingRange()],
  };
}

function restoreCamera(renderer: VtkScene['renderer'], state: CameraState) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cam = (renderer as any).getActiveCamera();
  cam.setPosition(...state.pos);
  cam.setFocalPoint(...state.fp);
  cam.setViewUp(...state.up);
  cam.setClippingRange(...state.cr);
}

function getActiveScalarEntry(
  dataset: VtkDatasetInfo,
  activeScalar: string | null,
) {
  if (activeScalar && dataset.pointData[activeScalar]) {
    return dataset.pointData[activeScalar];
  }

  return Object.values(dataset.pointData).find(
    (arr) => arr.numberOfComponents === 1,
  );
}

async function applyDatasetToScene(
  scene: VtkScene,
  dataset: VtkDatasetInfo,
  activeScalar: string | null,
  activeScalarLabel: string | null,
  initialCameraState: React.RefObject<CameraState | null>,
  meshVisible: boolean,
  forcedScalarRange: ScalarRange | null,
) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const vtk = await loadVtkModules();

  const hadPreviousData =
    ((scene.polydata as any).getPoints?.()?.getNumberOfPoints?.() ?? 0) > 0;

  const previousCamera = hadPreviousData ? captureCamera(scene.renderer) : null;

  const points = (vtk.vtkPoints as any).newInstance();
  points.setData(new Float32Array(dataset.points.flat()), 3);
  (scene.polydata as any).setPoints(points);

  if (dataset.cells.connectivity.length > 0) {
    const flatCells: number[] = [];
    for (const cell of dataset.cells.connectivity) {
      flatCells.push(cell.length, ...cell);
    }

    const cellArray = (vtk.vtkCellArray as any).newInstance({
      values: Uint32Array.from(flatCells),
    });

    (scene.polydata as any).setPolys(cellArray);
  } else {
    (scene.polydata as any).setPolys(
      (vtk.vtkCellArray as any).newInstance({ values: new Uint32Array() }),
    );
  }

  const scalarEntry = getActiveScalarEntry(dataset, activeScalar);

  if (scene.scalarBarActor) {
    (scene.renderer as any).removeActor2D(scene.scalarBarActor);
    (scene.scalarBarActor as any).delete?.();
    scene.scalarBarActor = null;
  }

  if (
    scalarEntry &&
    scalarEntry.numberOfComponents === 1 &&
    Array.isArray(scalarEntry.values) &&
    scalarEntry.values.length > 0
  ) {
    const scalarValues = scalarEntry.values as number[];

    (scene.polydata as any).getPointData().setScalars(
      (vtk.vtkDataArray as any).newInstance({
        name: scalarEntry.name,
        values: Float32Array.from(scalarValues),
        numberOfComponents: 1,
      }),
    );

    (scene.mapper as any).setScalarVisibility(true);
    (scene.mapper as any).setColorModeToMapScalars();
    (scene.mapper as any).setScalarModeToUsePointData();

    const resolvedRange = resolveScalarRange(scalarValues, forcedScalarRange);

    if (resolvedRange) {
      const lut = applyPaletteToMapper(vtk, scene.mapper, resolvedRange);
      replaceScalarBar(
        vtk,
        scene,
        lut,
        activeScalarLabel || scalarEntry.name || activeScalar || 'Scalar',
      );
    } else {
      (scene.mapper as any).setScalarVisibility(false);
    }
  } else {
    (scene.polydata as any).getPointData().setScalars(null);
    (scene.mapper as any).setScalarVisibility(false);
  }

  (scene.actor as any).setVisibility(meshVisible);

  (scene.polydata as any).modified();
  (scene.mapper as any).modified();

  if (previousCamera) {
    restoreCamera(scene.renderer, previousCamera);
  } else if (meshVisible) {
    (scene.renderer as any).resetCamera();
    (scene.renderer as any).resetCameraClippingRange();
    initialCameraState.current = captureCamera(scene.renderer);
  }

  (scene.renderWindow as any).render();
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

async function applyStreamlinesToScene(
  scene: VtkScene,
  streamlines: VtkPolylineSet | null,
  streamlineScalar: string | null,
  streamlineScalarLabel: string | null,
  showStreamlines: boolean,
  initialCameraState: React.RefObject<CameraState | null>,
) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const vtk = await loadVtkModules();

  const hadPreviousStreamlines =
    ((scene.streamlinePolydata as any).getPoints?.()?.getNumberOfPoints?.() ??
      0) > 0;

  (scene.streamlineActor as any).setVisibility(
    showStreamlines && !!streamlines,
  );

  if (!showStreamlines || !streamlines) {
    (scene.streamlinePolydata as any).setPoints(
      (vtk.vtkPoints as any).newInstance(),
    );
    (scene.streamlinePolydata as any).setLines(
      (vtk.vtkCellArray as any).newInstance({ values: new Uint32Array() }),
    );
    (scene.streamlinePolydata as any).getPointData().setScalars(null);
    (scene.streamlineMapper as any).setScalarVisibility(false);

    (scene.renderWindow as any).render();
    return;
  }

  const points = (vtk.vtkPoints as any).newInstance();
  points.setData(new Float32Array(streamlines.points.flat()), 3);
  (scene.streamlinePolydata as any).setPoints(points);

  const flatLines: number[] = [];
  for (const line of streamlines.lines) {
    flatLines.push(line.length, ...line);
  }

  (scene.streamlinePolydata as any).setLines(
    (vtk.vtkCellArray as any).newInstance({
      values: Uint32Array.from(flatLines),
    }),
  );

  const scalarEntry =
    streamlineScalar && streamlines.pointData[streamlineScalar]
      ? streamlines.pointData[streamlineScalar]
      : null;

  if (
    scalarEntry &&
    scalarEntry.numberOfComponents === 1 &&
    Array.isArray(scalarEntry.values) &&
    scalarEntry.values.length > 0
  ) {
    const scalarValues = scalarEntry.values as number[];

    (scene.streamlinePolydata as any).getPointData().setScalars(
      (vtk.vtkDataArray as any).newInstance({
        name: scalarEntry.name,
        values: Float32Array.from(scalarValues),
        numberOfComponents: 1,
      }),
    );

    (scene.streamlineMapper as any).setScalarVisibility(true);
    (scene.streamlineMapper as any).setColorModeToMapScalars();
    (scene.streamlineMapper as any).setScalarModeToUsePointData();

    const resolvedRange = resolveScalarRange(scalarValues, null);
    if (resolvedRange) {
      const lut = applyPaletteToMapper(
        vtk,
        scene.streamlineMapper,
        resolvedRange,
      );
      replaceScalarBar(
        vtk,
        scene,
        lut,
        streamlineScalarLabel ||
          scalarEntry.name ||
          streamlineScalar ||
          'Scalar',
      );
    }
  } else {
    (scene.streamlinePolydata as any).getPointData().setScalars(null);
    (scene.streamlineMapper as any).setScalarVisibility(false);
    (scene.streamlineActor as any).getProperty().setColor(1, 1, 1);

    if (scene.scalarBarActor) {
      (scene.renderer as any).removeActor2D(scene.scalarBarActor);
      (scene.scalarBarActor as any).delete?.();
      scene.scalarBarActor = null;
    }
  }

  (scene.streamlinePolydata as any).modified();
  (scene.streamlineMapper as any).modified();

  if (!hadPreviousStreamlines) {
    (scene.renderer as any).resetCamera();
    (scene.renderer as any).resetCameraClippingRange();
    initialCameraState.current = captureCamera(scene.renderer);
  }

  (scene.renderWindow as any).render();
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

export default forwardRef<VtkViewerHandle, Props>(function VtkViewer(
  {
    dataset,
    activeScalar = null,
    activeScalarLabel = null,
    streamlines = null,
    streamlineScalar = null,
    streamlineScalarLabel = null,
    showStreamlines = true,
    meshVisible = true,
    status = 'ready',
    forcedScalarRange = null,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<VtkScene | null>(null);
  const initialCameraState = useRef<CameraState | null>(null);
  const pipelineReady = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    let mounted = true;
    let resolveReady!: () => void;
    pipelineReady.current = new Promise<void>((resolve) => {
      resolveReady = resolve;
    });

    let resizeObserver: ResizeObserver | null = null;

    async function init() {
      const container = containerRef.current;
      if (!container) {
        resolveReady();
        return;
      }

      await loadVtkModules();
      if (!mounted) {
        resolveReady();
        return;
      }

      const scene = createVtkScene(await loadVtkModules(), container);
      sceneRef.current = scene;

      resizeObserver = new ResizeObserver(() => {
        scene.resize();
      });
      resizeObserver.observe(container);

      resolveReady();
    }

    init().catch((err) => {
      console.error('VtkViewer init error:', err);
      resolveReady();
    });

    return () => {
      mounted = false;
      resizeObserver?.disconnect();
      sceneRef.current?.destroy();
      sceneRef.current = null;
      initialCameraState.current = null;
      resolveReady();
    };
  }, []);

  useEffect(() => {
    if (!dataset) return;

    async function update() {
      await pipelineReady.current;
      if (!sceneRef.current) return;

      await applyDatasetToScene(
        sceneRef.current,
        dataset,
        activeScalar,
        activeScalarLabel,
        initialCameraState,
        meshVisible,
        forcedScalarRange,
      );
    }

    void update();
  }, [
    dataset,
    activeScalar,
    activeScalarLabel,
    meshVisible,
    forcedScalarRange,
  ]);

  useEffect(() => {
    async function update() {
      await pipelineReady.current;
      if (!sceneRef.current) return;

      await applyStreamlinesToScene(
        sceneRef.current,
        streamlines,
        streamlineScalar,
        streamlineScalarLabel,
        showStreamlines,
        initialCameraState,
      );
    }

    void update();
  }, [streamlines, streamlineScalar, streamlineScalarLabel, showStreamlines]);

  useImperativeHandle(ref, () => ({
    resetCamera() {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      const scene = sceneRef.current;
      if (!scene) return;

      if (initialCameraState.current) {
        restoreCamera(scene.renderer, initialCameraState.current);
      } else {
        (scene.renderer as any).resetCamera();
        (scene.renderer as any).resetCameraClippingRange();
      }

      (scene.renderWindow as any).render();
      /* eslint-enable @typescript-eslint/no-explicit-any */
    },
  }));

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 0,
        background: '#111',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {status === 'computing' && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 10,
            padding: '6px 10px',
            borderRadius: 8,
            background: 'rgba(0,0,0,0.55)',
            color: 'white',
            fontSize: 13,
          }}
        >
          Computing...
        </div>
      )}
    </div>
  );
});
